<template>
  <section class="bag-item__instructions">
    <VueAccordion
      :title="(specialInstructions !== '' && !accordionRef?.showPanel
      ? specialInstructionsAddedState : specialInstructionsLabelText)"
      :ariaTitle="(`${productId}-special-instructions`)"
      :defaltOpen="false"
      :buttonClass="'bag-item__instructions-btn'"
      ref="accordionRef"
    >
      <div class="bag-item__instructions-text-wrapper">
        <textarea
          class="bag-item__instructions-text"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          :value="specialInstructions"
          @input="handleChange"
          @blur="saveOnUnfocus"
          :placeholder="specialInstructionsPlaceholderText"
        >
        </textarea>
        <button
          class="bag-item__instructions-submit"
          @click="postNote"
        >
          <span>Save Note</span>
        </button>
      </div>
      <template #opened-icon>
        <i class="cart-upsell__header-icon icon icon--minus"></i>
        <span class="screenreader">Minimize Options</span>
      </template>
      <template #closed-icon>
        <span
        v-if="(specialInstructions === '')"
        >
          <i class="cart-upsell__header-icon icon icon--plus"></i>
          <span class="screenreader">Expand Options</span>
        </span>
        <span
          v-else
        >
          <span class="cart-upsell__header-icon">Edit</span>
        </span>
      </template>
    </VueAccordion>
    <div
      v-if="(specialInstructions !== '' && !accordionRef?.showPanel)"
    >
      <p class="bag-item__instructions-note">{{ specialInstructions }}</p>
    </div>
  </section>
</template>
<script>
import {
  toRefs,
  ref,
  onMounted,
  computed,
} from 'vue';
import useCart from '../composables/cart-actions';
import useFrameSpecData from '../composables/cart-frame-spec';
import VueAccordion from '../common/accordion/Accordion';

export default {

  name: 'CartItemInstructions',
  components: {
    VueAccordion,
  },
  props: {
    productId: {
      type: String,
      default: '',
      required: true,
    },
    productQty: {
      type: Number,
      default: 1,
      required: true,
    },
    productProperties: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    isWishlist: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const { updateCartItemById, fetchCart } = useCart();
    const { updateFramingSpecInstructions } = useFrameSpecData();
    const {
      productId,
      productProperties,
      productQty,
      isWishlist,
    } = toRefs(props);
    const isSaving = ref(false);
    const specialInstructions = ref('');
    const accordionRef = ref(false);

    const frameSpecNumber = computed(() => productProperties.value?._frame_spec_number || null);
    const frameSpecAuthToken = computed(() => productProperties.value?._frame_spec_authorization_token || null);

    const getInstructionsFromCartProperties = () => {
      // eslint-disable-next-line no-underscore-dangle
      return productProperties.value?._specialInstructionsMessage || '';
    };

    function decodeHTML(string) {
      return String(string)
        .replace(/&amp;/g, '&') // Converts &amp; to &
        .replace(/&quot;/g, '"') // Converts &quot; to "
        .replace(/&#39;/g, "'"); // Converts &#39; to '
    }
    const specialInstructionsLabelText = ref(
      decodeHTML(SDG.Data.cart.add_special_instructions_label_text),
    );
    const specialInstructionsAddedState = ref(
      decodeHTML(SDG.Data.cart.special_instructions_added_state_text),
    );
    const specialInstructionsPlaceholderText = ref(
      decodeHTML(SDG.Data.cart.special_instructions_placeholder_text),
    );

    onMounted(async () => {
      try {
        if (frameSpecNumber.value && frameSpecAuthToken.value) {
          const joineryHost = SDG?.Data?.joineryHost || 'https://staging.framebridge.com';
          const response = await fetch(
            `${joineryHost}/api/v1/framing_specifications/${frameSpecNumber.value}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Framebridge-Resource-Authorization-Token': frameSpecAuthToken.value,
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            specialInstructions.value = data.instructions || '';
          } else {
            specialInstructions.value = getInstructionsFromCartProperties();
          }
        } else {
          specialInstructions.value = getInstructionsFromCartProperties();
        }
      } catch (error) {
        console.error('Failed to load special instructions:', error);
        specialInstructions.value = getInstructionsFromCartProperties();
      }
    });

    function handleChange(event) {
      specialInstructions.value = event.target.value;
    }

    function saveOnUnfocus() {
      postNote();

      if (accordionRef.value && accordionRef.value.showPanel) {
        accordionRef.value.closePanel();
      }
    }

    async function postNote(event = null) {
      // stop double saving on unfocus
      if (isSaving.value) return;
      isSaving.value = true;

      const closeAcc = (success = true) => {
        if (success) {
          fetchCart(isWishlist.value);
        }
        if (accordionRef.value && accordionRef.value.showPanel) {
          accordionRef.value.closePanel();
        }
        isSaving.value = false;
      };

      try {
        if (frameSpecNumber.value && frameSpecAuthToken.value) {
          await updateFramingSpecInstructions(
            frameSpecNumber.value,
            frameSpecAuthToken.value,
            specialInstructions.value
          );
          closeAcc(true);
        } else {
          const properties = productProperties.value || {};
          const newProperties = {
            ...properties,
            _specialInstructions: 'true',
            _specialInstructionsMessage: specialInstructions.value,
          };

          updateCartItemById(
            event,
            productId.value,
            newProperties,
            productQty.value,
            isWishlist.value,
            false,
            closeAcc,
          );
        }
      } catch (error) {
        console.error('Failed to save special instructions:', error);
        closeAcc(false);
      }
    }

    return {
      accordionRef,
      specialInstructions,
      specialInstructionsLabelText,
      specialInstructionsAddedState,
      specialInstructionsPlaceholderText,
      handleChange,
      postNote,
      saveOnUnfocus,
      decodeHTML,
    };
  },
};
</script>
